/**
 * Global on-map hover-preview hook (one listener for both marker symbol
 * layers — task + note). Drives `state.ui.hoveredMarkerPreview`; the
 * `MarkerHoverPreview` component consumes that slot to render a single popup.
 *
 * Why one global listener instead of per-layer `mouseenter` (the previous
 * approach for the task layer): when two markers overlap geographically (e.g.
 * a note pin sitting on top of a task marker) MapLibre fires hover events on
 * BOTH layers and both popups appear. Using `mousemove` + a single
 * `queryRenderedFeatures` call against both layers lets us pick the topmost
 * feature deterministically and emit exactly one preview.
 *
 * @module components/map/hooks/useMapMarkerHoverPreview
 */

import { useEffect, type RefObject } from 'react';

import type { MapRef } from '@vis.gl/react-maplibre';
import type { MapMouseEvent } from 'maplibre-gl';
import type { Feature } from 'geojson';

import { CURSOR_DEFAULT, CURSOR_POINTER } from '@/constants/geometry';
import { useDeviceLayout } from '@/hooks/useDeviceLayout';
import { useGeoMarkupStore } from '@/store';
import { LAYER_IDS as TASK_LAYER_IDS } from '@/components/map/UnifiedAnchorSymbolLayer/styles/symbolLayerStyles';
import { OBSTRUCTION_LAYER_IDS } from '@/components/map/UnifiedAnchorSymbolLayer/styles/obstructionSymbolLayerStyles';
import { NOTE_LAYER_IDS } from '@/components/map/NoteSymbolLayer/styles/noteSymbolLayerStyles';
import type { HoveredMarkerKind } from '@/store/slices/uiSlice';
import type { Coordinates } from '@/types';

interface UseMapMarkerHoverPreviewOptions {
  mapRef: RefObject<MapRef | null>;
  isMapReady: boolean;
}

const TASK_MARKER_LAYER = TASK_LAYER_IDS.MARKER_COMPOSITE;
const NOTE_MARKER_LAYER = NOTE_LAYER_IDS.MARKER_PIN;
const OBSTRUCTION_MARKER_LAYER = OBSTRUCTION_LAYER_IDS.SYMBOL;

function resolveCoordinates(
  feature: Feature,
  fallback: { lat: number; lng: number }
): Coordinates {
  const geom = feature.geometry;
  if (geom.type === 'Point' && geom.coordinates.length >= 2) {
    return {
      lng: geom.coordinates[0] as number,
      lat: geom.coordinates[1] as number,
    };
  }
  return { lat: fallback.lat, lng: fallback.lng };
}

export function useMapMarkerHoverPreview({
  mapRef,
  isMapReady,
}: UseMapMarkerHoverPreviewOptions): void {
  const { hasHover } = useDeviceLayout();
  const setHoveredMarkerPreview = useGeoMarkupStore(
    (state) => state.ui.setHoveredMarkerPreview
  );

  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map || !isMapReady || !hasHover) return;

    const queryLayers = [
      TASK_MARKER_LAYER,
      NOTE_MARKER_LAYER,
      OBSTRUCTION_MARKER_LAYER,
    ];
    let pendingPoint: MapMouseEvent['point'] | null = null;
    let pendingLngLat: MapMouseEvent['lngLat'] | null = null;
    let rafHandle: number | null = null;
    let lastCursor = '';

    const setCursor = (next: string): void => {
      if (lastCursor === next) return;
      lastCursor = next;
      map.getCanvas().style.cursor = next;
    };

    const processMove = (): void => {
      rafHandle = null;
      const point = pendingPoint;
      const lngLat = pendingLngLat;
      pendingPoint = null;
      pendingLngLat = null;
      if (!point || !lngLat) return;

      // queryRenderedFeatures throws when any id in `layers` is unknown to
      // the style. Filter to existing layers — they may not be registered
      // yet (UnifiedAnchorSymbolLayer setup is async + sprite cache) or may be
      // missing during a 2D↔3D style swap.
      const existingLayers = queryLayers.filter((layerId) =>
        map.getLayer(layerId)
      );
      if (existingLayers.length === 0) {
        setHoveredMarkerPreview(null);
        return;
      }

      const features = map.queryRenderedFeatures(point, {
        layers: existingLayers,
      });
      const top = features[0];
      if (!top) {
        // Don't reset cursor here — other layers (lines/shapes) own theirs.
        setHoveredMarkerPreview(null);
        return;
      }

      const id = top.properties?.id;
      if (typeof id !== 'string') {
        setHoveredMarkerPreview(null);
        return;
      }

      const layerId = top.layer?.id;
      const kind: HoveredMarkerKind =
        layerId === NOTE_MARKER_LAYER
          ? 'note'
          : layerId === OBSTRUCTION_MARKER_LAYER
            ? 'obstruction'
            : 'task';

      setHoveredMarkerPreview({
        kind,
        id,
        coordinates: resolveCoordinates(top, {
          lat: lngLat.lat,
          lng: lngLat.lng,
        }),
      });
      setCursor(CURSOR_POINTER);
    };

    const handleMouseMove = (event: MapMouseEvent): void => {
      pendingPoint = event.point;
      pendingLngLat = event.lngLat;
      if (rafHandle === null) {
        rafHandle = requestAnimationFrame(processMove);
      }
    };

    const handleMouseOut = (): void => {
      if (rafHandle !== null) {
        cancelAnimationFrame(rafHandle);
        rafHandle = null;
      }
      pendingPoint = null;
      pendingLngLat = null;
      setHoveredMarkerPreview(null);
      setCursor(CURSOR_DEFAULT);
    };

    map.on('mousemove', handleMouseMove);
    map.on('mouseout', handleMouseOut);

    return () => {
      if (rafHandle !== null) cancelAnimationFrame(rafHandle);
      map.off('mousemove', handleMouseMove);
      map.off('mouseout', handleMouseOut);
      setHoveredMarkerPreview(null);
    };
  }, [mapRef, isMapReady, hasHover, setHoveredMarkerPreview]);
}
